import React, { useState, useEffect, useRef } from 'react';
import './TermsAndConditions.css';

/**
 * Terms & Conditions page for JG Printing.
 * Last updated: 2025
 */

const sections = [
    {
        id: 'acceptance-of-terms',
        title: 'Acceptance of Terms',
        content: `By accessing or using the JG Printing website and placing orders with us, you agree to be bound by these Terms and Conditions. If you do not agree with any part of these terms, please do not use our services.

These Terms and Conditions apply to all visitors, customers, and users of our website and services. JG Printing reserves the right to update or modify these terms at any time without prior notice.`
    },
    {
        id: 'orders-and-payments',
        title: 'Orders & Payments',
        content: `• All orders are subject to acceptance and availability.
• A 50% downpayment is required before production begins. The remaining balance must be settled before pickup or delivery.
• Accepted payment methods include cash, GCash, Maya, and bank transfer.
• Prices are subject to change without notice. The price confirmed at the time of your order will apply to that transaction.
• JG Printing reserves the right to cancel or refuse any order at its discretion.`
    },
    {
        id: 'design-and-files',
        title: 'Design & File Requirements',
        content: `• Customers are responsible for ensuring that submitted design files are correct, complete, and print-ready before approval.
• Accepted file formats: PDF, PNG, JPG, JPEG, AI. Files must be at least 300 DPI in CMYK color mode for best results.
• JG Printing is not liable for poor print quality resulting from low-resolution or incorrectly formatted files submitted by the customer.
• A digital proof will be provided for approval before production. Once the proof is approved, JG Printing is not responsible for errors the customer has approved.
• Design services are available for an additional fee. Design files created by JG Printing remain the intellectual property of JG Printing unless otherwise agreed in writing.`
    },
    {
        id: 'production-and-turnaround',
        title: 'Production & Turnaround Time',
        content: `• Standard production time is 3–5 business days from the date of proof approval and downpayment confirmation.
• Rush orders may be accommodated subject to availability and may incur additional fees. Please contact us in advance.
• JG Printing is not liable for delays caused by factors beyond our control, including but not limited to supplier delays, force majeure events, or incorrect information provided by the customer.
• Production begins only after downpayment is received and the design proof is approved by the customer.`
    },
    {
        id: 'delivery-and-pickup',
        title: 'Delivery & Pickup',
        content: `• Customers may pick up their orders at our shop: 42 Balagtas Street, Purok 4, Lower Bicutan, Taguig City, Philippines 1632.
• Delivery via third-party courier is available. All shipping fees are shouldered by the customer.
• JG Printing is not responsible for delays, damage, or loss incurred during third-party courier delivery. Claims for courier-related issues must be filed directly with the courier.
• Risk of loss or damage to the products passes to the customer upon handover to the courier or upon pickup at our shop.`
    },
    {
        id: 'returns-and-refunds',
        title: 'Returns & Refunds',
        content: `• Due to the custom nature of our products, all sales are generally final. We do not accept returns or offer refunds for orders that have been produced as approved.
• If you receive an order that is defective or significantly different from the approved proof, please contact us within 24 hours of receipt with photographic evidence.
• JG Printing will assess reported defects and, at our discretion, offer a reprint or partial refund where the error is determined to be on our part.
• No refunds or reprints will be issued for errors in customer-submitted designs that were approved prior to production.`
    },
    {
        id: 'intellectual-property',
        title: 'Intellectual Property',
        content: `• Customers must ensure they have the legal right to reproduce any logos, images, or artwork included in their submitted designs. JG Printing is not liable for any intellectual property infringement arising from customer-submitted content.
• By submitting a design to JG Printing, you confirm that you own or have obtained all necessary rights and permissions to use the design elements provided.
• JG Printing reserves the right to use photographs of completed orders for portfolio, marketing, and social media purposes unless the customer explicitly requests otherwise in writing.`
    },
    {
        id: 'limitation-of-liability',
        title: 'Limitation of Liability',
        content: `• JG Printing's total liability to you for any claim arising from the use of our services shall not exceed the total amount paid by you for the specific order in question.
• JG Printing is not liable for any indirect, incidental, special, or consequential damages, including loss of profit or business opportunity.
• We make no warranties, express or implied, beyond what is stated in these Terms and Conditions.`
    },
    {
        id: 'governing-law',
        title: 'Governing Law',
        content: `These Terms and Conditions shall be governed by and construed in accordance with the laws of the Republic of the Philippines. Any disputes arising from these terms or your use of our services shall be subject to the exclusive jurisdiction of the courts of Taguig City, Philippines.`
    },
    {
        id: 'contact',
        title: 'Contact Us',
        content: `If you have any questions or concerns about these Terms and Conditions, please contact us:

📧 Email: jgprinting2024@gmail.com
📘 Facebook: facebook.com/jgdigitalprinting
📍 Address: 42 Balagtas Street, Purok 4, Lower Bicutan, Taguig City, Philippines 1632

We aim to respond to all inquiries within 3–5 business days.`
    },
];

function TermsAndConditions() {
    const [activeSection, setActiveSection] = useState(sections[0].id);
    const observerRef = useRef(null);

    useEffect(() => {
        observerRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveSection(entry.target.id);
                    }
                });
            },
            { rootMargin: '-30% 0px -60% 0px' }
        );

        sections.forEach(({ id }) => {
            const el = document.getElementById(id);
            if (el) observerRef.current.observe(el);
        });

        return () => observerRef.current?.disconnect();
    }, []);

    const scrollToSection = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <main className="terms-page">

            {/* ── Hero ── */}
            <section className="terms-hero">
                <div className="terms-hero-inner">
                    <p className="terms-eyebrow">Legal</p>
                    <h1 className="terms-title">Terms &amp; Conditions</h1>
                    <p className="terms-subtitle">
                        Please read these terms carefully before placing an order or using
                        JG Printing's services. By using our website, you agree to be
                        bound by the following terms.
                    </p>
                    <p className="terms-effective">Effective date: January 1, 2025</p>
                </div>
                <div className="terms-hero-divider" />
            </section>

            {/* ── Body ── */}
            <div className="terms-body">
                <div className="terms-container">

                    {/* Sidebar TOC */}
                    <aside className="terms-toc">
                        <p className="terms-toc-label">On this page</p>
                        <nav>
                            {sections.map((s) => (
                                <button
                                    key={s.id}
                                    className={`terms-toc-item ${activeSection === s.id ? 'terms-toc-item--active' : ''}`}
                                    onClick={() => scrollToSection(s.id)}
                                >
                                    {s.title}
                                </button>
                            ))}
                        </nav>
                    </aside>

                    {/* Content */}
                    <article className="terms-content">
                        {sections.map((s, i) => (
                            <div key={s.id} id={s.id} className="terms-section">
                                <div className="terms-section-header">
                                    <span className="terms-section-number">
                                        {String(i + 1).padStart(2, '0')}
                                    </span>
                                    <h2 className="terms-section-title">{s.title}</h2>
                                </div>
                                <div className="terms-section-body">
                                    {s.content.split('\n').map((line, j) =>
                                        line.trim() === '' ? null : (
                                            <p key={j} className={line.startsWith('•') ? 'terms-bullet' : ''}>
                                                {line}
                                            </p>
                                        )
                                    )}
                                </div>
                            </div>
                        ))}
                    </article>

                </div>
            </div>

        </main>
    );
}

export default TermsAndConditions;